import { Col, Form, InputNumber, message, Row, Table, Tag } from 'antd';
import { useEffect, useRef } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { getCookie } from '../../../../services/cookies';
import NormalForm from './NormalForm';
import proceduresCodes from '../../../../utils/proceduresCodes.json';
import { getAsset } from '../../../../services/general';
import { useAudiometryChart } from '../../hooks/useAudiometryChart';
import { handleVerticalTab } from './audiometry/audiometryUtils';

const CATEGORIES = [125, 250, 500, 1000, 2000, 3000, 4000, 6000, 8000];

const AudiometryForm = ({
  fromExternal,
  inputs,
  disableForm,
  form,
  chartRef,
  audiometryState,
  setAudiometry,
  roles,
  fileList,
  loadingFiles,
  status,
  setFileList,
  toDoctor,
  removeDocument,
  convertFile,
  convertError,
  isDICOM,
  handleConvertFile,
  imageUrl,
  setImageUrl,
  setShowAudiometryChartRef,
  countrySettings,
}) => {
  const { chartImage, showChart, clearChartImage } = useAudiometryChart(inputs);
  const countryCode = getCookie('country_code');

  useEffect(() => {
    if (setShowAudiometryChartRef) {
      setShowAudiometryChartRef(showChart);
    }
  }, [setShowAudiometryChartRef, showChart]);

  useEffect(() => {
    const IncAudi = inputs.procedures.find(
      (procedure) =>
        procedure.code === proceduresCodes.audiometria['Inc. Auditiva'] &&
        procedure.result,
    );
    const IncGan = inputs.procedures.find(
      (procedure) =>
        procedure.code === proceduresCodes.audiometria['Inc. Ganancia'] &&
        procedure.result,
    );
    const OD500 = inputs.procedures.find(
      (procedure) =>
        procedure.code ===
        proceduresCodes.audiometria['average_od_500_to_2000'] &&
        procedure.result,
    );
    const OD1000 = inputs.procedures.find(
      (procedure) =>
        procedure.code ===
        proceduresCodes.audiometria['average_od_1000_to_6000'] &&
        procedure.result,
    );
    const OI500 = inputs.procedures.find(
      (procedure) =>
        procedure.code ===
        proceduresCodes.audiometria['average_oi_500_to_2000'] &&
        procedure.result,
    );
    const OI1000 = inputs.procedures.find(
      (procedure) =>
        procedure.code ===
        proceduresCodes.audiometria['average_oi_1000_to_6000'] &&
        procedure.result,
    );

    if (
      audiometryState.extra_data &&
      !audiometryState.extra_data.frecuencies_od &&
      !audiometryState.extra_data.frecuencies_oi &&
      inputs.extra_data &&
      inputs.extra_data.frecuencies_od &&
      inputs.extra_data.frecuencies_oi
    ) {
      const object = CATEGORIES.reduce(
        (accumulated, current, index) => ({
          ...accumulated,
          [current]: {
            OD: JSON.parse(inputs.extra_data.frecuencies_od)[
              `frequency_${index}`
            ],
            OI: JSON.parse(inputs.extra_data.frecuencies_oi)[
              `frequency_oi_${index}`
            ],
          },
        }),
        {},
      );
      form.setFieldsValue(object);
    }

    setAudiometry((audiometry) => ({
      ...audiometry,
      IncAudi:
        IncAudi && IncAudi.result && IncAudi.result.trim()
          ? IncAudi.result
          : '0.0',
      IncGan:
        IncGan && IncGan.result && IncGan.result.trim() ? IncGan.result : '0.0',
      '500_2000OD':
        OD500 && OD500.result && OD500.result.trim() ? OD500.result : '0.0',
      '1000_6000OD':
        OD1000 && OD1000.result && OD1000.result.trim() ? OD1000.result : '0.0',
      '500_2000OI':
        OI500 && OI500.result && OI500.result.trim() ? OI500.result : '0.0',
      '1000_6000OI':
        OI1000 && OI1000.result && OI1000.result.trim() ? OI1000.result : '0.0',
      extra_data: {
        frecuencies_od:
          audiometry.extra_data.frecuencies_od ||
          (inputs.extra_data && inputs.extra_data.frecuencies_od),
        frecuencies_oi:
          audiometry.extra_data.frecuencies_oi ||
          (inputs.extra_data && inputs.extra_data.frecuencies_oi),
      },
    }));
  }, [inputs.state, inputs.procedures, setAudiometry]);

  const average = (values) => {
    if (!values || values.length === 0) {
      return 0;
    }
    const sum = values.reduce((prev, acc) => prev + acc, 0);
    return sum / values.length;
  };

  const getMinMaxHearingLoss = (avgOD, avgOI) => {
    const min = Math.min(avgOD, avgOI);
    const max = Math.max(avgOD, avgOI);
    return [Math.max((min - 25) * 1.5, 0), Math.max((max - 25) * 1.5, 0)];
  };

  const graphicData = () => {
    const data = [];
    const values = form.getFieldsValue();
    Object.keys(values).forEach((value) => {
      if (CATEGORIES.includes(parseInt(value))) {
        const obj = {};
        obj.name = value;
        obj.OD = values[value].OD;
        obj.OI = values[value].OI;
        data.push(obj);
      }
    });

    return data;
  };

  const handleChange = () => {
    clearChartImage(); // Use this instead of setChartImage()

    const values = form.getFieldsValue();

    let frecuencies_od = {};
    let frecuencies_oi = {};

    const avg500_2000 = { valuesOD: [], valuesOI: [] };
    const avg1000_6000 = { valuesOD: [], valuesOI: [] };

    // The calculation logic remains mostly the same, but we ensure it's safe
    Object.keys(values).forEach((value, index) => {
      if (!CATEGORIES.includes(parseInt(value))) return; // Ignore non-frequency fields

      const odValue = values[value]?.OD;
      const oiValue = values[value]?.OI;

      if (500 <= parseInt(value) && parseInt(value) <= 2000) {
        avg500_2000.valuesOD.push(parseInt(odValue) || 0);
        avg500_2000.valuesOI.push(parseInt(oiValue) || 0);
      }
      if (1000 <= parseInt(value) && parseInt(value) <= 6000) {
        avg1000_6000.valuesOD.push(parseInt(odValue) || 0);
        avg1000_6000.valuesOI.push(parseInt(oiValue) || 0);
      }
      frecuencies_od = {
        ...frecuencies_od,
        ...((odValue || odValue === 0) && {
          [`frequency_${index}`]: odValue.toString(),
        }),
      };

      frecuencies_oi = {
        ...frecuencies_oi,
        ...((oiValue || oiValue === 0) && {
          [`frequency_oi_${index}`]: oiValue.toString(),
        }),
      };
    });

    avg500_2000.OD = average(avg500_2000.valuesOD);
    avg500_2000.OI = average(avg500_2000.valuesOI);
    avg1000_6000.OD = average(avg1000_6000.valuesOD);
    avg1000_6000.OI = average(avg1000_6000.valuesOI);

    const [minHearingLoss, maxHearingLoss] = getMinMaxHearingLoss(
      avg1000_6000.OD,
      avg1000_6000.OI,
    );
    const loss = (minHearingLoss * 5 + maxHearingLoss) / 6;
    const gain = Math.min(loss * 0.65, 65);

    setAudiometry((audiometry) => ({
      ...audiometry,
      IncAudi: loss.toFixed(2),
      IncGan: gain.toFixed(2),
      '500_2000OD': avg500_2000.OD.toFixed(2),
      '1000_6000OD': avg1000_6000.OD.toFixed(2),
      '500_2000OI': avg500_2000.OI.toFixed(2),
      '1000_6000OI': avg1000_6000.OI.toFixed(2),
      extra_data: {
        frecuencies_od: JSON.stringify(frecuencies_od),
        frecuencies_oi: JSON.stringify(frecuencies_oi),
      },
    }));
  };

  const inputRefs = useRef({});
  const handleFocusNext = (col, row) => {
    inputRefs.current[`${col}-${row}`]?.focus();
  };

  return (
    <Row justify='center' gutter={[0, 30]}>
      <Col lg={24} xs={24}>
        <Row justify='space-between'>
          <Col lg={fromExternal ? 24 : 10} xs={24} className='col-container'>
            <Row justify='center'>
              <Col lg={22} xs={22}>
                <h2>Datos</h2>
                <Table
                  className='cmt-table'
                  dataSource={CATEGORIES.map((value) => ({ value }))}
                  pagination={false}
                  rowKey='value'
                >
                  <Table.Column
                    title='Frecuencias'
                    align='center'
                    width='40%'
                    dataIndex='value'
                  />
                  {['OD', 'OI'].map((type, columnIndex) => (
                    <Table.Column
                      title={type}
                      className={type}
                      key={columnIndex}
                      align='center'
                      width='30%'
                      render={(text, record, index) => (
                        <Form.Item
                          className='mb-0'
                          name={[text.value, type]}
                          initialValue={0}
                          required={true}
                          rules={[
                            {
                              required: true,
                              message: 'Requerido',
                            },
                          ]}
                        >
                          <InputNumber
                            ref={(element) => (inputRefs.current[`${columnIndex}-${index}`] = element)}
                            onKeyDown={(event) =>
                              handleVerticalTab(
                                event,
                                { colIndex: columnIndex, rowIndex: index },
                                { totalCols: 2, totalRows: CATEGORIES.length },
                                handleFocusNext
                              )
                            }
                            onFocus={(event) => event.target.select()}
                            onChange={handleChange}
                            disabled={fromExternal || disableForm}
                            controls={false}
                            precision={2}
                            className='cmt-input-number'
                          />
                        </Form.Item>
                      )}
                    />
                  ))}
                </Table>
              </Col>
            </Row>
          </Col>
          <Col
            lg={fromExternal ? 24 : 13}
            xs={24}
            className='audiometry-chart-container'
          >
            {!fromExternal ? (
              <div className='audiometry-chart responsive-chart-wrapper'>
                <ResponsiveContainer
                  debounce={300}
                >
                  <LineChart
                    ref={chartRef}
                    data={graphicData()}
                    margin={{
                      top: 20,
                      right: 70,
                      left: 0,
                      bottom: 35,
                    }}
                  >
                    <CartesianGrid />
                    <XAxis dataKey='name' />
                    <YAxis
                      domain={[-10, 120]}
                      reversed={true}
                      tickCount={14}
                      minTickGap={10}
                      interval={0}
                    />
                    <Legend
                      align='left'
                      verticalAlign='top'
                      height={36}
                      iconSize={0}
                      wrapperStyle={{ paddingLeft: '4%', paddingBottom: '10%' }}
                    />
                    <Line
                      strokeWidth={2}
                      type='monotone'
                      dataKey='OD'
                      stroke='#AF5253'
                      animationDuration={500}
                      animationEasing='linear'
                      dot={{ stroke: '#AF5253', strokeWidth: 5, r: 2 }}
                    />
                    <Line
                      strokeWidth={2}
                      type='monotone'
                      dataKey='OI'
                      stroke='#638EAB'
                      animationDuration={500}
                      animationEasing='linear'
                      dot={({ cx, cy, stroke }) => (
                        <svg
                          x={cx - 7}
                          y={cy - 8}
                          width={30}
                          height={30}
                          fill='#638EAB'
                          viewBox='0 0 1024 1024'
                          key={stroke + cx + cy}
                        >
                          <path d='M384.955,256l120.28-120.28c9.019-9.019,9.019-23.642,0-32.66L408.94,6.765 c-9.019-9.019-23.642-9.019-32.66,0l-120.28,120.28L135.718,6.765c-9.019-9.019-23.642-9.019-32.66,0L6.764,103.058 c-9.019,9.019-9.019,23.642,0,32.66l120.28,120.28L6.764,376.28c-9.019,9.019-9.019,23.642,0,32.66l96.295,96.294 c9.019,9.019,23.642,9.019,32.66,0l120.28-120.28l120.28,120.28c9.019,9.019,23.642,9.019,32.66,0l96.295-96.294 c9.019-9.019,9.019-23.642,0-32.66L384.955,256z' />
                        </svg>
                      )}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <img src={chartImage} alt='Gráfico de audiometría' />
            )}
            <Row gutter={20}>
              {['500 - 1000 - 2000', '1000 - 2000 - 3000 - 4000 - 6000'].map(
                (type, typeIndex) => (
                  <Col lg={12} className='avg-col-container' key={typeIndex}>
                    <h3>Promedios</h3>
                    <Row className='pt-15'>
                      <Col lg={24} className='avg-header pl-15'>
                        <p>{type}</p>
                      </Col>
                      <Col lg={24} className='pt-15 pl-15'>
                        {['OD', 'OI'].map((oType, oTypeIndex) => (
                          <Row justify='space-between' key={oTypeIndex}>
                            <Col lg={12}>
                              <p className={oType}>{oType}</p>
                            </Col>
                            <Col lg={12}>
                              <Row justify='end'>
                                <Tag>
                                  {type === '500 - 1000 - 2000'
                                    ? audiometryState[`500_2000${oType}`]
                                    : audiometryState[`1000_6000${oType}`]}
                                </Tag>
                              </Row>
                            </Col>
                          </Row>
                        ))}
                      </Col>
                    </Row>
                  </Col>
                ),
              )}
            </Row>
          </Col>
        </Row>
        <Row className='mt-20' justify='space-between'>
          <Col lg={fromExternal ? 24 : 10} className='col-container'>
            <Row justify='center'>
              <Col lg={22}>
                <h2>Resultados</h2>
              </Col>
            </Row>
            {['CL'].includes(countryCode) && (
              <Row className='mt-20' justify='center'>
                <Col lg={22}>
                  <Row justify='space-between'>
                    <Col lg={12}>
                      <p>Inc. Ganancia</p>
                    </Col>
                    <Col lg={12}>
                      <Row justify='end'>
                        <Tag
                          className={status(audiometryState.IncGan, 'IncGan')}
                        >
                          {audiometryState.IncGan}
                        </Tag>
                      </Row>
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col lg={12}>
                      <p>Inc. Auditiva</p>
                    </Col>
                    <Col lg={12}>
                      <Row justify='end'>
                        <Tag>{audiometryState.IncAudi}</Tag>
                      </Row>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
            <Row className='results-row mt-20' />
            <Row className='mt-20' justify='center'>
              <Col lg={22}>
                <NormalForm
                  form={form}
                  fromExternal={fromExternal}
                  inputs={inputs}
                  disableForm={disableForm}
                  roles={roles}
                  status={status}
                  loadingFiles={loadingFiles}
                  setFileList={setFileList}
                  fileList={fileList}
                  toDoctor={toDoctor}
                  removeDocument={removeDocument}
                  convertFile={convertFile}
                  convertError={convertError}
                  isDICOM={isDICOM}
                  handleConvertFile={handleConvertFile}
                  imageUrl={imageUrl}
                  setImageUrl={setImageUrl}
                  countrySettings={countrySettings}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default AudiometryForm;
